// src/pages/Users/Inscription/RenewalModal.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { updateAdherentGroup, updateAdherent } from "./../../../services/adherentServices";
import { getActiveGroupsList } from "./../../../services/groupServices";
import Button from "../../../components/Button/Button";
import { FaSave, FaTimes } from "react-icons/fa";
import { toast } from "react-toastify";

const RenewalModal = ({ isOpen, onClose, adherent, nextSeason }) => {
  const [groups, setGroups] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState("");
  const [updatedBirthYears, setUpdatedBirthYears] = useState("");
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [filter, setFilter] = useState("");

  const selectRef = useRef(null);

  // Charger groupes actifs
  useEffect(() => {
    if (!isOpen) return;
    (async () => {
      try {
        setLoadingGroups(true);
        const activeGroups = await getActiveGroupsList();
        setGroups(Array.isArray(activeGroups) ? activeGroups : []);
      } catch (error) {
        console.error("Erreur lors de la récupération des groupes actifs:", error);
        toast.error("Impossible de charger les groupes actifs.");
      } finally {
        setLoadingGroups(false);
      }
    })();
  }, [isOpen]);

  // Focus initial sur le select
  useEffect(() => {
    if (isOpen && selectRef.current) {
      setTimeout(() => selectRef.current?.focus(), 50);
    }
  }, [isOpen, loadingGroups]);

  // Fermer par ESC
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => e.key === "Escape" && onClose(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  // Filtrage simple des groupes
  const filteredGroups = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return groups;
    return groups.filter((g) =>
      `${g.nom || ""} ${g.annees_naissance || ""}`.toLowerCase().includes(q)
    );
  }, [groups, filter]);

  const handleGroupChange = (e) => {
    const groupId = e.target.value;
    setSelectedGroupId(groupId);

    const selectedGroup = groups.find((g) => String(g.id) === String(groupId));
    if (selectedGroup) {
      const { annees_naissance } = selectedGroup;
      // On affiche tel quel (ton besoin initial)
      setUpdatedBirthYears(annees_naissance || "");
    } else {
      setUpdatedBirthYears("");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedGroupId) {
      toast.warn("Veuillez sélectionner un groupe.");
      return;
    }
    const groupId = parseInt(selectedGroupId, 10);
    if (Number.isNaN(groupId)) {
      toast.error("L'ID de groupe sélectionné n'est pas valide.");
      return;
    }

    try {
      setIsSaving(true);

      // 1) Mettre la nouvelle saison + statut Inscrit
      await updateAdherent(adherent.id, {
        saison_active: nextSeason,
        statut_inscription: "Inscrit",
      });

      // 2) Affecter le groupe
      await updateAdherentGroup(adherent.id, groupId);

      const selectedGroup = groups.find((g) => g.id === groupId);
      toast.success("Renouvellement enregistré.");
      onClose(true, selectedGroup);
    } catch (err) {
      console.error(err);
      toast.error("Erreur lors de l’enregistrement du renouvellement.");
    } finally {
      setIsSaving(false);
    }
  };

    const toAbsolute = (u) => {
    if (!u) return "";
    const s = String(u).trim();
    // déjà absolu, data URL ou blob
    if (/^https?:\/\//i.test(s) || s.startsWith("data:") || s.startsWith("blob:")) return s;
    // chemins d'assets front (ex: /img/...): on laisse tel quel
    if (!s.startsWith("/uploads")) return s;
    // fichiers uploadés côté API
    const base = process.env.REACT_APP_API_URL?.replace(/\/$/, "") || "";
    return `${base}${s}`;
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[100] bg-slate-900/50 backdrop-blur-sm grid place-items-center px-3"
      onClick={() => onClose(false)}
      aria-modal="true"
      role="dialog"
      aria-labelledby="renewal-title"
    >
      <div
        className="w-full max-w-xl rounded-2xl overflow-hidden shadow-2xl ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/90 dark:bg-slate-900/70"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header dégradé */}
        <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 relative">
          <h3
            id="renewal-title"
            className="px-6 py-4 pr-14 text-white text-base sm:text-lg font-semibold text-center"
          >
            Renouvellement pour la saison {nextSeason}
          </h3>
          <button
            type="button"
            onClick={() => onClose(false)}
            className="absolute right-3 top-1/2 -translate-y-1/2 inline-flex items-center justify-center h-9 w-9 rounded-full bg-white/20 hover:bg-white/30 text-white transition"
            aria-label="Fermer"
            title="Fermer"
          >
            <FaTimes />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="px-6 py-5">
          {/* Récap adhérent */}
          <div className="mb-4 flex items-center gap-3">
            <img
              src={
                toAbsolute(adherent?.photo_url) ||
                (adherent?.genre === "Féminin"
                  ? "/img/judokate_avatar.png"
                  : "/img/judoka_avatar.png")
              }
              alt={`${adherent?.nom || ""} ${adherent?.prenom || ""}`}
              className="h-12 w-12 rounded-full object-cover ring-1 ring-slate-200 dark:ring-slate-700"
            />
            <div className="min-w-0">
              <div className="font-semibold text-slate-900 dark:text-slate-100 truncate">
                {(adherent?.nom || "").toUpperCase()} {adherent?.prenom || ""}
              </div>
              <div className="text-xs text-slate-500 dark:text-slate-400">
                Saison actuelle : {adherent?.saison_active || "—"}
              </div>
            </div>
          </div>

          {/* Saisie */}
          <div className="grid grid-cols-1 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                Nouvelle saison
              </label>
              <input
                type="text"
                value={nextSeason}
                readOnly
                className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                Sélectionnez un année de naissance ou un groupe
              </label>

              {/* Filtre simple */}
              <input
                type="text"
                placeholder="Filtrer par nom ou années…"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="mb-2 w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2"
              />

              <select
                ref={selectRef}
                value={selectedGroupId}
                onChange={handleGroupChange}
                disabled={loadingGroups}
                className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2"
              >
                <option value="">
                  {loadingGroups ? "Chargement des groupes…" : "Sélectionnez un groupe"}
                </option>
                {filteredGroups.map((group) => (
                  <option key={group.id} value={group.id}>
                    {group.nom} {group.annees_naissance ? `• ${group.annees_naissance}` : ""}
                  </option>
                ))}
              </select>
            </div>

            {updatedBirthYears && (
              <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50/70 dark:bg-slate-900/40 px-3 py-2">
                <p className="text-sm text-slate-700 dark:text-slate-300">
                  Années de naissance pour la saison {nextSeason} :{" "}
                  <span className="font-medium">{updatedBirthYears}</span>
                </p>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="mt-5 flex justify-center gap-2">
            <Button
              text={isSaving ? "Enregistrement…" : "Enregistrer"}
              icon={FaSave}
              type="submit"
              disabled={isSaving || !selectedGroupId}
              className="!rounded-xl gap-2 disabled:opacity-60"
            />
            <Button
              text="Annuler"
              icon={FaTimes}
              type="button"
              onClick={() => onClose(false)}
              className="!rounded-xl gap-2"
            />
          </div>
        </form>
      </div>
    </div>
  );
};

export default RenewalModal;
